import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react'

import styled, { css, useTheme } from 'styled-components'

import config from '@config'
import { getUserType } from '@utils/chatbot'
import { getLocalStorageItem, setLocalStorageItem } from '@utils/localStorage'
import translate from '@i18n'
import useUser from '@hooks/useUser'
import JSONRenderer from '@components/JSONRenderer'
import SvgIcon from '@components/SvgIcon'

import { AnswerFeedbackProps } from '../types'
import OnboardingFeedbackTrigger from './OnboardingFeedbackTrigger'

const {
  usersnap: { rateLimitLocalStorageKey, rateLimitPerMinute },
} = config

const StyledAnswerFeedback = styled.div`
  ${({
    theme: {
      colors: { fillDefault, strokeWeak },
      spacing: { spacing24 },
    },
  }) => css`
    margin-top: ${spacing24};
    padding-top: ${spacing24};
    border-top: 1px solid ${strokeWeak};
    position: relative;
    background-color: ${fillDefault};
  `}
`

const StyledAnswerFeedbackQuestionButtons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
`

const StyledRatingQuestion = styled.div<{
  breakWords: AnswerFeedbackProps['breakWords']
}>`
  ${({
    theme: {
      typography: {
        bodycopy: {
          xxsmall: { bundledCSS: bodyCopyXXSmallCSS },
        },
      },
      spacing: { spacing24 },
      colors: { textWeak },
    },
    breakWords,
  }) => css`
    color: ${textWeak};
    margin-right: ${spacing24};
    ${bodyCopyXXSmallCSS}
    ${breakWords &&
    css`
      word-break: break-word;
      margin-right: 0;
      hyphens: auto;
    `}
  `}
`

const StyledIconsWrapper = styled.div<{
  isRatingEnabled: boolean
  buttonsPosition: AnswerFeedbackProps['feedbackButtonsPosition']
}>`
  ${({
    isRatingEnabled,
    buttonsPosition,

    theme: {
      spacing: { spacing24 },
    },
  }) => css`
    display: flex;
    gap: ${spacing24};
    opacity: 1;
    overflow: hidden;
    min-width: 200px;
    ${buttonsPosition === 'end' &&
    css`
      justify-content: flex-end;
    `}

    ${isRatingEnabled &&
    css`
      animation: fadeIn 0.5s ease-in-out forwards;
      @keyframes fadeIn {
        0% {
          opacity: 0;
        }
        100% {
          opacity: 1;
        }
      }
    `}
  `}
`

const StyledRatingButton = styled.button<{
  isRatingSelected: boolean
}>`
  ${({
    isRatingSelected,
    theme: {
      colors: {
        fillDefaultDisabled,
        textDefault,
        textDefaultDisabled,
        fillStrongHover,
        fillBrand,
        textDefaultHover,
      },

      spacing: { spacing8 },
      typography: {
        bodycopy: {
          xxsmall: { bundledCSS: bodyCopyXXSmallCSS },
        },
      },
    },
  }) => css`
    display: flex;
    align-items: center;
    gap: ${spacing8};
    cursor: pointer;
    border: none;
    padding: 0;
    height: 40px;
    background-color: transparent;
    color: ${textDefault};
    ${bodyCopyXXSmallCSS}

    &:disabled {
      cursor: not-allowed;
      color: ${isRatingSelected ? textDefault : textDefaultDisabled};
    }
    &:disabled > div {
      background-color: ${isRatingSelected ? fillBrand : fillDefaultDisabled};
    }
    @media (hover: hover) {
      &:hover:not(:disabled) {
        color: ${textDefaultHover};
      }
      &:hover:not([disabled]) > div {
        background-color: ${isRatingSelected ? fillBrand : fillStrongHover};
      }
    }
  `}
`

const StyledButtonIconWrapper = styled.div<{
  isRatingSelected: boolean
}>`
  ${({
    isRatingSelected,
    theme: {
      colors: { fillStrong, fillDefaultDisabled, fillBrand },
    },
  }) => css`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: ${isRatingSelected ? fillBrand : fillStrong};
    &:disabled {
      background-color: ${fillDefaultDisabled};
    }
  `}
`

const StyledFeedbackWrapper = styled.div<{ isRatingVisible: boolean }>`
  ${({
    isRatingVisible,
    theme: {
      spacing: { spacing16 },
    },
  }) => css`
    display: flex;
    flex-direction: column;
    gap: ${spacing16};
    opacity: 0;
    height: 0;
    overflow: hidden;
    transition: height 0.3s ease-in-out;
    ${isRatingVisible &&
    css`
      height: auto;
      animation: fadeIn 1s ease-in-out forwards;
      @keyframes fadeIn {
        100% {
          opacity: 1;
        }
      }
    `};
  `}
`

const StyledFeedbackDivider = styled.div`
  ${({
    theme: {
      spacing: { spacing24 },
    },
  }) => css`
    padding-top: ${spacing24};
  `}
`

export const UsersnapRating = {
  Positive: 1,
  Neutral: 0,
  Negative: -1,
} as const

type UsersnapRating = (typeof UsersnapRating)[keyof typeof UsersnapRating]

const getMinuteTimestamp = () => {
  const now = new Date()
  now.setSeconds(0, 0) // Set seconds and milliseconds to zero
  return now.getTime()
}

const getFeedbackCount = (): number => {
  const timestampedCount =
    getLocalStorageItem<string>(`usersnapFeedbackCount`)?.toString() ?? ''
  const timestampCount = timestampedCount.split('.')

  // If the timestamp is not within ttl minute, reset the count
  if (
    timestampCount.length !== 2 ||
    parseInt(timestampCount[0], 10) !== getMinuteTimestamp()
  ) {
    setFeedbackCount(0)
    return 0
  }

  const number = parseInt(timestampCount[1], 10)
  return Math.max(Math.min(isNaN(number) ? 0 : number, rateLimitPerMinute), 0)
}

const setFeedbackCount = (count: number) => {
  setLocalStorageItem(
    rateLimitLocalStorageKey,
    `${getMinuteTimestamp()}.${count}`,
  )
}

const AnswerFeedback: FunctionComponent<AnswerFeedbackProps> = ({
  onboardingInProcess,
  customUsersnapData,
  usersnapKey,
  className,
  customFeedbackText,
  trackingFunction,
  feedbackButtonsPosition = 'default',
  breakWords,
  showFeedbackText = true,
  showButtonLabels = true,
  children,
}) => {
  const [feedbackGiven, setFeedbackGiven] = useState<boolean>(false)
  const [isRatingEnabled, setIsRatingEnabled] = useState<boolean>(true)
  const [isFeedbackLimitReached, setIsFeedbackLimitReached] =
    useState<boolean>(false)
  const theme = useTheme()
  const feedbackRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setIsFeedbackLimitReached(getFeedbackCount() === rateLimitPerMinute)
  }, [])

  const [rating, setRating] = useState<UsersnapRating>(UsersnapRating.Neutral)
  const user = useUser()

  const ratingCallback = useMemo(
    () => (rating: UsersnapRating) => () => {
      const nextCount = getFeedbackCount() + 1

      trackingFunction(
        rating === UsersnapRating.Positive ? 'positive' : 'negative',
      )

      setRating(rating)
      setFeedbackGiven(false)
      setFeedbackCount(nextCount)
      setIsRatingEnabled(false)
    },
    [setRating, setFeedbackGiven, trackingFunction],
  )

  const ratingIconNotSelectedColor = useMemo(
    () =>
      rating === UsersnapRating.Neutral
        ? theme.colors.fillInverse
        : theme.colors.staticNeutral600,
    [rating, theme.colors.fillInverse, theme.colors.staticNeutral600],
  )

  const isPositiveRatingSelected = useMemo(
    () => rating === UsersnapRating.Positive,
    [rating],
  )

  const isNegativeRatingSelected = useMemo(
    () => rating === UsersnapRating.Negative,
    [rating],
  )

  const textRatingsData = useMemo(
    () => ({
      kind: ['widget', 'tool', 'usersnap'],
      id: usersnapKey,
      custom: {
        rating: rating === UsersnapRating.Positive ? 'positive' : 'negative',
        time: new Date().getTime(),
        userType: getUserType(user?.email),
        ...customUsersnapData,
      } satisfies Record<string, string | number | boolean>,
    }),
    [rating, user, customUsersnapData, usersnapKey],
  )

  return (
    <StyledAnswerFeedback className={className} ref={feedbackRef}>
      {!isFeedbackLimitReached && (
        <StyledAnswerFeedbackQuestionButtons>
          {showFeedbackText && (
            <StyledRatingQuestion breakWords={breakWords}>
              {customFeedbackText || translate('chatbot.answerFeedback.text')}
            </StyledRatingQuestion>
          )}
          <StyledIconsWrapper
            isRatingEnabled={isRatingEnabled}
            buttonsPosition={feedbackButtonsPosition}
          >
            <StyledRatingButton
              disabled={!isRatingEnabled}
              onClick={ratingCallback(UsersnapRating.Positive)}
              isRatingSelected={isPositiveRatingSelected}
            >
              <StyledButtonIconWrapper
                isRatingSelected={isPositiveRatingSelected}
              >
                <SvgIcon
                  iconName="thumbs-up"
                  color={
                    isPositiveRatingSelected
                      ? theme.colors.staticWhite
                      : ratingIconNotSelectedColor
                  }
                  size={20}
                />
              </StyledButtonIconWrapper>
              {showButtonLabels && translate('yes')}
            </StyledRatingButton>
            <StyledRatingButton
              disabled={!isRatingEnabled}
              onClick={ratingCallback(UsersnapRating.Negative)}
              isRatingSelected={isNegativeRatingSelected}
            >
              <StyledButtonIconWrapper
                isRatingSelected={isNegativeRatingSelected}
              >
                <SvgIcon
                  iconName="thumbs-down"
                  color={
                    isNegativeRatingSelected
                      ? theme.colors.staticWhite
                      : ratingIconNotSelectedColor
                  }
                  size={20}
                />
              </StyledButtonIconWrapper>
              {showButtonLabels && translate('no')}
            </StyledRatingButton>
          </StyledIconsWrapper>
          {children}
        </StyledAnswerFeedbackQuestionButtons>
      )}
      <StyledFeedbackWrapper
        isRatingVisible={rating !== UsersnapRating.Neutral && !feedbackGiven}
      >
        <StyledFeedbackDivider />
        <JSONRenderer>{textRatingsData}</JSONRenderer>
      </StyledFeedbackWrapper>
      {onboardingInProcess && (
        <OnboardingFeedbackTrigger feedbackRef={feedbackRef} />
      )}
    </StyledAnswerFeedback>
  )
}

export default AnswerFeedback
